import { Fragment, Suspense } from "react";

import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { expectContent } from "@/content/utils/expectContent";
import { useLocalizedHref } from "@/libraries/web-framework";
import AgileFooter from "@/patterns/agile-footer/src";
import AgilePageLayout from "@/patterns/agile-page-layout/src";
import ArticleShare from "@/patterns/article-share/src";
import GlobalNavigation from "@/patterns/global-nav/src";
import { SubNavSoftwareDevelopment } from "@/patterns/sub-nav/src/subnav-software-development";
import { FourOhFourPageTemplate } from "@/templates/404/404-page/src/404-page.template";
import { AgileBreadcrumbs } from "@/templates/agile-hub/src/components/agile-breadcrumbs/src/agile-breadcrumbs";
import { useLocation } from "@/utils/location-provider";

import AgileHeading from "../../../patterns/agile-heading/src";
import { AgileArticle } from "../types";

import { useAgileArticle } from "./agile-article.provider";
import AgileIntroduction from "./components/agile-introduction/src";
import { AgileAuthor } from "./sections/agile-author";
import { RichTextComponent } from "./sections/components/body-text";
import { BodyTextContainer } from "./sections/main-content/body-text-container";
import { NavigationCards } from "./sections/navigation-cards";
import { ShareUpNextBar } from "./sections/share-up-next";
import { useAgileHubPageTreeData } from "./use-agile-hub-page-tree-data";

export const AgileArticlePageTemplate = () => {
    return (
        <ContentNotFoundErrorBoundary fallback={<FourOhFourPageTemplate />}>
            <Suspense>
                <AgileArticlePageTemplateContent />
            </Suspense>
        </ContentNotFoundErrorBoundary>
    );
};

const AgileArticlePageTemplateContent = () => {
    const agileArticleData = useAgileArticle();
    if (!agileArticleData) {
        return null;
    }

    return <AgileArticlePageTemplateContentDetails {...agileArticleData} />;
};

const AgileArticlePageTemplateContentDetails = (props: AgileArticle) => {
    const { title, subtitle, author, articleBanner, nextBestAction } = props;
    const location = useLocation();
    const origin = "https://www.atlassian.com";
    const link = `${origin}${location.pathname}`;

    return (
        <>
            <GlobalNavigation
                isShowTryNowLink={true}
                tryNowLinkCtaUrl={useLocalizedHref("/try/cloud/signup")}
                tryNowLinkCtaButtonColor="blue"
                productKey="agile"
            />
            <SubNavSoftwareDevelopment />
            <AgileBreadcrumbs />
            <AgileHeading
                title={expectContent(title, "Article content not found")}
                description={subtitle}
                author={author}
                imageUrl={articleBanner?.url}
            />
            <AgilePageLayout productDiscussedCta={props.productDiscussedCta}>
                {props.articleSummary && (
                    <RichTextComponent
                        text={props.articleSummary}
                        name={null}
                    />
                )}
                {props.articleIntroduction?.length &&
                    props.articleIntroduction.map((data, idx) => (
                        <Fragment key={idx}>
                            <AgileIntroduction
                                heading={data.articleIntroductionName}
                                image={data.articleIntroductionAsset}
                                embedLink={data.embedLink}
                                description={data.articleIntroductionDetails}
                                body={data.componentizedIntroduction}
                            />
                        </Fragment>
                    ))}
                {props.bodyTextContainer?.map((data, idx) => (
                    <div key={idx}>
                        <BodyTextContainer
                            name={data.name}
                            contentComponents={expectContent(
                                data.contentComponents,
                                "Article content not found"
                            )}
                        />
                    </div>
                ))}
                <ArticleShare
                    description={props.description}
                    title={title}
                    link={link}
                />
                <AgileAuthor
                    name={author?.name}
                    biography={author?.shortBiography}
                    pictureSrc={author?.authorPicture?.url}
                    contentType={author?.authorPicture?.contentType}
                />
            </AgilePageLayout>
            {nextBestAction && nextBestAction.length && (
                <NavigationCards cardsData={nextBestAction} />
            )}

            <ShareUpNextBar
                nodes={useAgileHubPageTreeData()}
                title={props.title}
                description={props.description}
            />
            <AgileFooter />
        </>
    );
};
